/**
 * רשימת מוצרים — TASKS 3.3. עימוד שרת (25 לדף), סינון וחיפוש.
 */
import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { isAdminApiError } from './adminApi.js';
import { type Category, listCategories } from './categoriesApi.js';
import { MagnifyingGlass, PencilSimple, Trash } from '@phosphor-icons/react';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import {
  deleteProduct,
  getProduct,
  listProducts,
  updateProduct,
  updateVariant,
  type ProductListItem,
  type Variant,
} from './productsApi.js';

const PRICING_TYPE_LABEL: Record<string, string> = {
  unit: 'יחידה',
  per_meter: 'לפי מטר',
};

export function ProductsPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<ProductListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [q, setQ] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // ⭐ 24.08.2026 — פידבק אריאנה: הרשימה הראתה רק את שורת המוצר, ⛔ לא את
  // הוואריאנטים שלו, כך שנראה כאילו יש 11 מוצרים בלבד בעוד שבפועל יש עשרות
  // ואריאנטים כל אחד עם מחיר משלו. טל צריך לראות ולערוך כל וריאנט ישירות
  // מכאן, ⛔ בלי להיכנס ל"עריכה" של כל מוצר בנפרד. נטען אחרי טעינת המוצרים -
  // לא שאילתה אחת מרוכזת, כי `GET /api/admin/products` (רשימה) לא מחזיר
  // ואריאנטים בכוונה (ביצועים, ראו `ProductListItem` ב-`productsApi.ts`).
  const [variantsByProduct, setVariantsByProduct] = useState<Record<number, Variant[]>>({});

  useEffect(() => {
    listCategories()
      .then(setCategories)
      .catch(() => {
        // ⭐ כשל בטעינת הקטגוריות ⛔ אינו חוסם את רשימת המוצרים — הסינון פשוט לא יופיע
      });
  }, []);

  async function refetch() {
    setError(null);
    try {
      const result = await listProducts({
        page,
        categoryId: categoryId === '' ? undefined : categoryId,
        isActive: activeFilter === 'all' ? undefined : activeFilter === 'active',
        q: q.trim() || undefined,
      });
      setProducts(result.products);
      setTotal(result.total);
      setPageSize(result.pageSize);
      await refetchVariants(result.products);
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת המוצרים.');
    }
  }

  async function refetchVariants(productList: ProductListItem[]) {
    const entries = await Promise.all(
      productList.map(async (product) => {
        try {
          const { variants } = await getProduct(product.id);
          return [product.id, variants] as const;
        } catch {
          // ⛔ כשל בטעינת ואריאנטים למוצר בודד ⛔ אינו חוסם את שאר הרשימה
          return [product.id, []] as const;
        }
      }),
    );
    setVariantsByProduct(Object.fromEntries(entries));
  }

  async function handleVariantPriceUpdate(productId: number, variant: Variant, rawValue: string) {
    setError(null);
    const trimmed = rawValue.trim();
    const price = trimmed === '' ? null : Number(trimmed);
    if (price !== null && (Number.isNaN(price) || price < 0)) {
      setError('מחיר הוואריאנט חייב להיות מספר חיובי.');
      return;
    }
    try {
      await updateVariant(productId, variant.id, { price });
      const { variants } = await getProduct(productId);
      setVariantsByProduct((prev) => ({ ...prev, [productId]: variants }));
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בעדכון מחיר הוואריאנט.');
    }
  }

  useEffect(() => {
    void refetch();
  }, [page, categoryId, activeFilter]);

  function handleSearchSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    void refetch();
  }

  async function handleToggleActive(product: ProductListItem) {
    setError(null);
    try {
      await updateProduct(product.id, { isActive: !product.is_active });
      await refetch();
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בעדכון המוצר.');
    }
  }

  async function handleDelete(id: number) {
    setError(null);
    try {
      await deleteProduct(id);
      setConfirmDeleteId(null);
      await refetch();
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה במחיקת המוצר.');
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <AdminPageHeader
          title="מוצרים"
          description="ניהול המוצרים בקטלוג - מחירים, תמונות, ספסיפיקציות ותוספות."
          action={
            <Link to="/admin/products/new" className="btn btn-cta">
              מוצר חדש
            </Link>
          }
        />

        <form
          className="mt-6 grid gap-4 sm:grid-cols-[2fr_1fr_1fr_auto]"
          onSubmit={handleSearchSubmit}
          role="search"
        >
          <div>
            <label htmlFor="prod-q" className="block text-sm font-semibold">
              חיפוש (שם או מק"ט)
            </label>
            <div className="relative mt-1">
              <MagnifyingGlass
                size={16}
                weight="regular"
                aria-hidden="true"
                className="text-muted pointer-events-none absolute start-3 top-1/2 -translate-y-1/2"
              />
              <input
                id="prod-q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-md border border-line py-2 pe-3 ps-9"
              />
            </div>
          </div>

          <div>
            <label htmlFor="prod-category" className="block text-sm font-semibold">
              קטגוריה
            </label>
            <select
              id="prod-category"
              value={categoryId}
              onChange={(e) => {
                setPage(1);
                setCategoryId(e.target.value === '' ? '' : Number(e.target.value));
              }}
              className="mt-1 w-full rounded-md border border-line px-3 py-2"
            >
              <option value="">הכול</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="prod-status" className="block text-sm font-semibold">
              סטטוס
            </label>
            <select
              id="prod-status"
              value={activeFilter}
              onChange={(e) => {
                setPage(1);
                setActiveFilter(e.target.value as 'all' | 'active' | 'inactive');
              }}
              className="mt-1 w-full rounded-md border border-line px-3 py-2"
            >
              <option value="all">הכול</option>
              <option value="active">פעילים</option>
              <option value="inactive">מוסתרים</option>
            </select>
          </div>

          <div className="flex items-end">
            <button type="submit" className="btn btn-secondary">
              חיפוש
            </button>
          </div>
        </form>

        {error && (
          <p role="alert" className="text-danger mt-4 text-sm">
            {error}
          </p>
        )}

        {products === null && !error && <p className="text-muted mt-6">טוען…</p>}
        {products && products.length === 0 && <p className="text-muted mt-6">לא נמצאו מוצרים.</p>}

        {products && products.length > 0 && (
          <>
            <table className="admin-table mt-6 w-full border-collapse text-sm">
              <caption className="sr-only">
                רשימת מוצרים, עמוד {page} מתוך {totalPages}
              </caption>
              <thead>
                <tr className="border-line border-b text-start">
                  <th scope="col" className="p-2 text-start">
                    מק"ט
                  </th>
                  <th scope="col" className="p-2 text-start">
                    שם
                  </th>
                  <th scope="col" className="p-2 text-start">
                    קטגוריה
                  </th>
                  <th scope="col" className="p-2 text-start">
                    סוג תמחור
                  </th>
                  <th scope="col" className="p-2 text-start">
                    מחיר בסיס
                  </th>
                  <th scope="col" className="p-2 text-start">
                    סטטוס
                  </th>
                  <th scope="col" className="p-2 text-start">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const variants = variantsByProduct[product.id] ?? [];
                  return (
                    <Fragment key={product.id}>
                      <tr className="border-line border-b">
                        <td className="p-2">
                          <span className="ltr-inline">{product.sku}</span>
                        </td>
                        <td className="p-2">{product.name}</td>
                        <td className="p-2">{product.category_name}</td>
                        <td className="p-2">{PRICING_TYPE_LABEL[product.pricing_type]}</td>
                        <td className="tabular p-2">₪{product.base_price}</td>
                        <td className="p-2">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => void handleToggleActive(product)}
                          >
                            {product.is_active ? 'פעיל' : 'מוסתר'}
                          </button>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            {/* ⭐ DESIGN.md 8.3 — "עמודת פעולות: אייקוני Phosphor
                                עם aria-label, ⛔ לא טקסט 'ערוך | מחק' צפוף". */}
                            <Link
                              to={`/admin/products/${product.id}/edit`}
                              aria-label="עריכה"
                              className="flex h-11 w-11 items-center justify-center rounded border border-line"
                            >
                              <PencilSimple size={16} weight="regular" aria-hidden="true" />
                            </Link>

                            {confirmDeleteId === product.id ? (
                              <>
                                <button
                                  type="button"
                                  className="text-danger rounded border border-line px-2 py-1"
                                  onClick={() => void handleDelete(product.id)}
                                >
                                  לאשר מחיקה
                                </button>
                                <button
                                  type="button"
                                  className="rounded border border-line px-2 py-1"
                                  onClick={() => setConfirmDeleteId(null)}
                                >
                                  ביטול
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                aria-label="מחיקה"
                                className="flex h-11 w-11 items-center justify-center rounded border border-line"
                                onClick={() => setConfirmDeleteId(product.id)}
                              >
                                <Trash size={16} weight="regular" aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* ⭐ 24.08.2026 — כל ואריאנט מקבל שורה משלו, כדי שטל יראה
                          וייערוך מחיר ספציפי בלי להיכנס ל"עריכה" של המוצר. */}
                      {variants.map((variant) => (
                        <tr key={variant.id} className="border-line bg-surface border-b text-sm">
                          <td className="p-2 ps-6">
                            <span className="ltr-inline text-muted">↳ {variant.sku}</span>
                          </td>
                          <td className="text-muted p-2" colSpan={3}>
                            {variant.label}
                          </td>
                          <td className="p-2">
                            {variant.price_on_request ? (
                              <span className="text-muted">לפי בקשה</span>
                            ) : (
                              <form
                                className="flex items-center gap-2"
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  const input = e.currentTarget.elements.namedItem(
                                    `pv-price-${variant.id}`,
                                  ) as HTMLInputElement;
                                  void handleVariantPriceUpdate(product.id, variant, input.value);
                                }}
                              >
                                <input
                                  name={`pv-price-${variant.id}`}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  defaultValue={variant.price ?? ''}
                                  aria-label={`מחיר ${variant.sku} (₪, לפני מע"מ)`}
                                  className="bg-canvas tabular ltr-inline w-24 rounded-md border border-line px-2 py-1 text-sm"
                                />
                                <button
                                  type="submit"
                                  className="rounded border border-line px-2 py-1 text-sm"
                                >
                                  עדכון מחיר
                                </button>
                              </form>
                            )}
                          </td>
                          <td className="text-muted p-2">{variant.is_active ? 'פעיל' : 'מוסתר'}</td>
                          <td className="p-2" />
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>

            <nav aria-label="עימוד רשימת מוצרים" className="mt-4 flex items-center gap-3">
              <button
                type="button"
                className="rounded border border-line px-3 py-1 disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                הקודם
              </button>
              <span className="text-sm">
                עמוד {page} מתוך {totalPages} ({total} מוצרים)
              </span>
              <button
                type="button"
                className="rounded border border-line px-3 py-1 disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                הבא
              </button>
            </nav>
          </>
        )}
      </main>
    </>
  );
}
